/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { appendObjectValuesToFormData } from '@veone/ngx-utilities/form';
import dayjs from 'dayjs';
import { Observable, map } from 'rxjs';
import {
  Banner,
  BannerListParams,
  Pagination,
  BannerCreationParams,
  Redirection,
  RedirectionType,
  RedirectionValue
} from '~core/models/core.models';
import { ApiConstantsService } from './api-constants.service';
import { ConstantsService } from './constants.service';

const EXPIRED_AT_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';

@Injectable()
export class BannerService {
  private _webPlatformUrl = 'rti.ci';

  protected _httpClient = inject(HttpClient);
  protected _constantsService = inject(ConstantsService);
  protected _apiConstantsService = inject(ApiConstantsService);
  protected _domainName = this._constantsService.environment.domainName;

  read(bannerId: string): Observable<Banner> {
    return this._httpClient.get<Banner>(
      `${this._apiConstantsService.bannerUrl}/${bannerId}`
    );
  }

  list(params?: BannerListParams): Observable<Pagination<Banner>> {
    return this._httpClient
      .get<Pagination<Banner>>(this._apiConstantsService.bannerUrl, {
        params: {
          ...params,
          sort: params?.sort || 'createdAt',
          desc: params?.desc || 'createdAt',
          details: ''
        } as Record<string, any>
      })
      .pipe(
        map(res => {
          const banners = res.items.map(banner => {
            if (!banner.redirection || Array.isArray(banner.redirection)) {
              return banner;
            }

            if (banner.redirection.type === RedirectionType.external) {
              const isInternalUrl = banner.redirection.value.includes(
                this._webPlatformUrl
              );

              if (isInternalUrl) {
                banner.redirection.routerLink = this._getRouterLinkFromUrl(
                  banner.redirection.value
                );
              } else {
                banner.redirection.link!.href = this._formatRedirectionUrl(
                  banner.redirection
                );
              }
              return banner;
            }

            return banner;
          });
          res.items = [...banners];
          return res;
        })
      );
  }

  create(data: BannerCreationParams): Observable<Banner> {
    let formData: BannerCreationParams | FormData = {
      ...data,
      ...(data.expiresAt && {
        expiresAt: dayjs(data.expiresAt).format(EXPIRED_AT_DATE_FORMAT)
      })
    };

    if (typeof data.attachment !== 'string' && data.attachment?.file) {
      formData = new FormData();
      appendObjectValuesToFormData(formData, {
        ...data,
        ...(data.expiresAt && {
          expiresAt: dayjs(data.expiresAt).format(EXPIRED_AT_DATE_FORMAT)
        })
      });
    }

    return this._httpClient.post<Banner>(
      this._apiConstantsService.bannerUrl,
      formData
    );
  }

  createAttachmentBanner(data: BannerCreationParams): Observable<Banner> {
    const formData = new FormData();
    appendObjectValuesToFormData(formData, {
      ...data,
      ...(data.expiresAt && {
        expiresAt: dayjs(data.expiresAt).format(EXPIRED_AT_DATE_FORMAT)
      })
    });

    return this._httpClient.post<Banner>(
      `${this._apiConstantsService.bannerUrl}/with-attachment-file`,
      formData
    );
  }

  update(
    bannerId: string,
    data: Partial<BannerCreationParams>
  ): Observable<Banner> {
    let formData: FormData | Partial<BannerCreationParams> = {
      ...data,
      ...(data.expiresAt && {
        expiresAt: dayjs(data.expiresAt).format(EXPIRED_AT_DATE_FORMAT)
      })
    };
    if (
      'logo' in data ||
      (typeof data.attachment !== 'string' && data.attachment?.file)
    ) {
      formData = new FormData();
      appendObjectValuesToFormData(formData, {
        ...data,
        ...(data.expiresAt && {
          expiresAt: dayjs(data.expiresAt).format(EXPIRED_AT_DATE_FORMAT)
        })
      });
    }

    return this._httpClient.post<Banner>(
      `${this._apiConstantsService.bannerUrl}/${bannerId}`,
      formData
    );
  }

  delete(bannerId: string): Observable<unknown> {
    return this._httpClient.delete(
      `${this._apiConstantsService.bannerUrl}/${bannerId}`
    );
  }

  removeFromCarousel(attachmentId: string): Observable<unknown> {
    return this._httpClient.delete(
      `${this._apiConstantsService.bannerUrl}/remove-from-carousel/attachments/${attachmentId}`
    );
  }

  protected _formatRedirectionUrl(redirectionDetails: Redirection): string {
    let url: string;
    switch (redirectionDetails.type) {
      case RedirectionType.external:
        url = redirectionDetails.value;
        break;
      case RedirectionType.internal:
        url = this._getRedirectionValueUrl(
          redirectionDetails.value as RedirectionValue
        );
        break;
      case RedirectionType.program:
        url = redirectionDetails.link?.name
          ? `/programmes/${redirectionDetails.link.name}`
          : '';
        break;
      case RedirectionType.content:
        url = redirectionDetails.link?.name
          ? `/replays/${redirectionDetails.link.name}`
          : '';
        break;
      default:
        url = '';
        break;
    }

    return url;
  }

  protected _getRedirectionValueUrl(page: RedirectionValue): string {
    let url: string;
    switch (page) {
      case RedirectionValue.talent:
        url = '/talents';
        break;
      case RedirectionValue.contactUs:
        url = '/contactez-nous';
        break;
      case RedirectionValue.live:
        url = '/direct';
        break;
      case RedirectionValue.news:
        url = '/actualites';
        break;
      default:
        url = '/';
        break;
    }
    return url;
  }

  protected _getRouterLinkFromUrl(url: string): string {
    const startIndex = url.indexOf(this._domainName);
    if (startIndex === -1) return '/';
    const sliceStartIndex = startIndex + this._domainName.length;
    return decodeURIComponent(url.slice(sliceStartIndex));
  }
}
